import { useState, useCallback, useEffect, useMemo } from 'react'

import { useApp } from '../../../app/AppContext.jsx'

const STORAGE_KEY = 'bim_ca_supplements'

function normalizeCrmSupplementProduct(crmProduct, categoryKey) {
  return {
    id: String(crmProduct.crm_id_unit ?? crmProduct.crm_id_original ?? ''),
    crm_id_unit: crmProduct.crm_id_unit ?? null,
    category: categoryKey,
    name: crmProduct.cms_product_name ?? crmProduct.crm_name_original ??'N/A',
    area_original: crmProduct.area_original ?? null,
    price_brutto: crmProduct.price_brutto ?? null,
    price_m2_brutto: crmProduct.price_m2_brutto ?? null,
    status: crmProduct.status ?? 'unknown',
    visibility: crmProduct.visibility ?? 'unknown',
    source: 'crm',
  }
}

function loadPersistedIds(projectId, productKey) {
  if (!projectId || !productKey) return new Set()

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()

    const allProjects = JSON.parse(raw)
    const projectBucket = allProjects?.[projectId]
    const productBucket = projectBucket?.[productKey]

    if (!productBucket || typeof productBucket !== 'object') return new Set()

    const ids = Object.values(productBucket).flatMap((value) => Array.isArray(value) ? value : [])
    return new Set(ids.map(String))
  } catch {
    return new Set()
  }
}

function persistSelection(projectId, productKey, selectedItems, selectableCategories) {
  if (!projectId || !productKey) return

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const allProjects = raw ? JSON.parse(raw) : {}
    const projectBucket = allProjects?.[projectId] && typeof allProjects[projectId] === 'object'
      ? allProjects[projectId]
      : {}

    const nextProductBucket = {}
    for (const categoryKey of selectableCategories) {
      nextProductBucket[categoryKey] = []
    }

    for (const item of selectedItems) {
      if (!item?.category || !Array.isArray(nextProductBucket[item.category])) continue
      nextProductBucket[item.category].push(item.id)
    }

    projectBucket[productKey] = nextProductBucket
    allProjects[projectId] = projectBucket
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allProjects))
  } catch {
    // localStorage may be unavailable in private mode or blocked by the browser.
  }
}

/**
 * useProductSupplements — supplement selection state for one product.
 *
 * Reads available supplements from the already-loaded CRM payload exposed by CatalogContext.
 * The hook keeps user selections in localStorage so revisiting the same product restores them.
 */
export function useProductSupplements(product, panelSettings, rawCmsData) {
  const { windowData } = useApp()

  const projectId = windowData?.projectId ?? null
  const productKey = product?.uuid ?? product?.id_cad ?? null

  const [userSelectedIds, setUserSelectedIds] = useState(() => loadPersistedIds(projectId, productKey))
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  useEffect(() => {
    setUserSelectedIds(loadPersistedIds(projectId, productKey))
    setIsPickerOpen(false)
  }, [projectId, productKey])

  const selectableCategories = useMemo(
    () => panelSettings?.additional_unit_information?.supplement_purchases_options?.selectable_tables ?? [],
    [panelSettings],
  )

  const forcedItems = useMemo(
    () => (product?.supplementary_products ?? []).filter((s) => s.is_forced),
    [product],
  )

  const availableItems = useMemo(() => {
    if (!rawCmsData) return []
    if (!Array.isArray(selectableCategories) || selectableCategories.length === 0) return []

    const items = []

    for (const categoryKey of selectableCategories) {
      const categoryProducts = rawCmsData?.[categoryKey]
      if (!Array.isArray(categoryProducts)) continue

      for (const crmProduct of categoryProducts) {
        if (crmProduct?.visibility !== 'visible' || crmProduct?.price_visibility === false) continue
        const normalized = normalizeCrmSupplementProduct(crmProduct, categoryKey)
        if (normalized.id) {
          items.push(normalized)
        }
      }
    }

    return items
  }, [rawCmsData, selectableCategories])

  const selectedItems = useMemo(
    () => availableItems.filter((item) => userSelectedIds.has(item.id)),
    [availableItems, userSelectedIds],
  )

  const totalAdditive = useMemo(() => {
    const forcedSum = forcedItems
      .filter((s) => !s.is_price_included && s.price_brutto != null)
      .reduce((sum, s) => sum + s.price_brutto, 0)

    const selectedSum = selectedItems
      .filter((s) => s.price_brutto != null)
      .reduce((sum, s) => sum + s.price_brutto, 0)

    return forcedSum + selectedSum
  }, [forcedItems, selectedItems])

  const addItem = useCallback((id) => {
    setUserSelectedIds((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      persistSelection(projectId, productKey, availableItems.filter((item) => next.has(item.id)), selectableCategories)
      return next
    })
  }, [availableItems, projectId, productKey, selectableCategories])

  const removeItem = useCallback((id) => {
    setUserSelectedIds((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      persistSelection(projectId, productKey, availableItems.filter((item) => next.has(item.id)), selectableCategories)
      return next
    })
  }, [availableItems, projectId, productKey, selectableCategories])

  const openPicker = useCallback(() => setIsPickerOpen(true), [])
  const closePicker = useCallback(() => setIsPickerOpen(false), [])

  return {
    forcedItems,
    selectedItems,
    availableItems,
    totalAdditive,
    isPickerOpen,
    addItem,
    removeItem,
    openPicker,
    closePicker,
    selectableCategories,
  }
}